import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import Swal from "sweetalert2";
import { toast } from "react-hot-toast";

const ContactSection = () => {
  const [footer, setFooter] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Get Today's Date in YYYY-MM-DD format for the 'min' attribute
  const today = new Date().toISOString().split("T")[0];

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    date: "",
    page_url: "",
    doctor_name: "Footer Appointment Form", // Hidden value
    query: "",
    consent: true, // Checkbox state
  });
  const [submitting, setSubmitting] = useState(false);

  const resolveUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/")) {
      return `${process.env.REACT_APP_WEB_URL || ""}${url}`;
    }
    return url;
  };

  useEffect(() => {
    let cancelled = false;
    const fetchFooter = async () => {
      try {
        const res = await api.get("/api/global/footer-contact");
        const data = res.data || {};
        const fd = data.footer_data ?? null;

        if (cancelled) return;

        if (fd) {
          setFooter(fd);
        } else {
          setFooter(null);
        }
      } catch (err) {
        console.error("Error fetching footer data:", err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    fetchFooter();
    return () => {
      cancelled = true;
    };
  }, []);

  // Form Handlers with Inline Validation Logic
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
      return;
    }

    // 1. Name Validation: Only allow letters, spaces, and dots.
    if (name === "name") {
      const filteredValue = value.replace(/[^a-zA-Z\s.]/g, "");
      if (filteredValue.length <= 75) {
        setFormData({ ...formData, [name]: filteredValue });
      }
      return;
    }

    // 2. Phone Validation: Only allow numbers.
    if (name === "phone") {
      const filteredValue = value.replace(/\D/g, "");
      if (filteredValue.length <= 15) {
        setFormData({ ...formData, [name]: filteredValue });
      }
      return;
    }

    // 3. Query Validation: Max 500 characters.
    if (name === "query") {
      if (value.length <= 500) {
        setFormData({ ...formData, [name]: value });
      }
      return;
    }

    // Default for other fields (like date)
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast.error("Please provide your consent to proceed.");
      return;
    }

    if (formData.phone.length < 10) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        patient_name: formData.name,
        phone: formData.phone,
        date: formData.date,
        page_url: window.location.href,
        doctor_name: formData.doctor_name,
        query: formData.query,
      };

      await api.post(`/api/send-appointment`, payload);
      
      await Swal.fire({
        title: "Success!",
        text: "Appointment sent successfully!",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
        width: 450, 
      });
      
      setFormData({ 
        name: "", 
        phone: "", 
        date: "", 
        page_url: "", 
        doctor_name: "Footer Appointment Form", 
        query: "",
        consent: true 
      });
    } catch (err) {
      console.error("Submission error:", err);
      toast.error("Failed to send appointment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!loaded) return null;
  if (error) return null;
  if (!footer) return null;

  const {
    logo_url,
    description,
    address_line_1,
    address_line_2,
    email,
    phone_numbers = [],
    quick_links = [],
    social_links = [],
  } = footer;

  const quickLinksArray = Object.values(quick_links);

  const socialIconFor = (platform) => {
    switch ((platform || "").toLowerCase()) {
      case "facebook": return "fa-brands fa-facebook-f";
      case "instagram": return "fa-brands fa-instagram";
      case "youtube": return "fa-brands fa-youtube";
      case "linkedin": return "fa-brands fa-linkedin-in";
      case "twitter": return "fa-brands fa-twitter";
      default: return "fa-solid fa-globe";
    }
  };

  return (
    <section className="contact-section">
      <div className="container">
        <div className="row">
          <div className="col-md-3">
            {logo_url && (
              <div className="logo" data-sal="slide-up" data-sal-duration="1000" data-sal-delay="100">
                <img src={resolveUrl(logo_url)} alt="Organization Logo" />
              </div>
            )}
            {description && (
              <div 
                className="footer-description"
                data-sal="slide-up"
                data-sal-duration="300"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>

          <div className="col-md-3 mt-3 mt-md-5 ps-3 ps-md-5" data-sal="slide-up" data-sal-duration="1000" data-sal-delay="200">
            <h5>Contact Us</h5>
            <ul className="list-unstyled contact-info mb-3">
              {(address_line_1 || address_line_2) && (
                <li className="mb-2">
                  <i className="fa-solid fa-location-dot me-2"></i>
                  {address_line_1}
                  {address_line_2 ? <><br />{address_line_2}</> : null}
                </li>
              )}
              {email && (
                <li className="mb-2">
                  <i className="fa-solid fa-envelope me-2"></i>
                  <a href={`mailto:${email}`}>{email}</a>
                </li>
              )}
              {phone_numbers.length > 0 && (
                <li className="mb-2">
                  <i className="fa-solid fa-phone me-2"></i>
                  <div className="d-flex flex-column">
                    {phone_numbers.map((p, i) => (
                      <a key={i} href={`tel:${p.link || p.number || ""}`}>
                        {p.number || p.link}
                      </a>
                    ))}
                  </div>
                </li>
              )}
            </ul>
            <h5>Chennai Branch</h5>
            <ul className="list-unstyled contact-info mb-3">
              <li className="mb-2">
                <i className="fa-solid fa-location-dot me-2"></i>
               47/3, New Avadi Road,Kilpauk, <br /> Chennai, Tamil Nadu 600010.
              </li>
              {/* <li className="mb-2">
                <i className="fa-solid fa-envelope me-2"></i>
                <a href={`mailto:infohyd@llh.bz`}>infohyd@llh.bz</a>
              </li> */}
              <li className="mb-2">
                <i className="fa-solid fa-phone me-2"></i>
                <div className="d-flex flex-column">
                  <a href={`tel:+919677222333`}>96 77 222 333</a>
                </div>
              </li>
              <li className="mb-2">
                <i className="fa-solid fa-globe me-2"></i>
                <a className="text-decoration-underline" target='_blank' rel='noopener noreferrer' href='https://thelifelinehospitals.com/'>lifelinehospitals</a>
              </li>
            </ul>
          </div>

          <div className="col-md-2 mt-5" data-sal="slide-up" data-sal-duration="1000" data-sal-delay="300">
            <h5>{footer.quick_links_heading || "Quick Links"}</h5>
            <ul className="footer-links gap-3">
              {quickLinksArray.map((ql, i) => {
                const url = ql.url || "#";
                return (
                  <li key={i}>
                    {url.startsWith("/") ? <Link to={url}>{ql.text}</Link> : <a href={url} target="_blank" rel="noopener noreferrer">{ql.text}</a>}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="col-md-4 mt-5" data-sal="slide-up" data-sal-duration="1000" data-sal-delay="400">
            <div className="appointment-box">
              <h3 className="form-header">Book an Appointment</h3>
              <div className="p-4">
                <form onSubmit={handleSubmit} className="d-none d-md-block ">
                  <input type="hidden" name="doctor_name" value={formData.doctor_name} />
 
                  <input 
                    type="text" 
                    name="name"
                    className="form-control mb-2" 
                    placeholder="Name:" 
                    value={formData.name}
                    onChange={handleChange}
                    maxLength="75"
                    required 
                  />
                
                  <input 
                    type="text" 
                    name="phone"
                    className="form-control mb-2" 
                    placeholder="Phone No:" 
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength="15"
                    required 
                  />
                
                  <input 
                    type="date" 
                    name="date"
                    className="form-control mb-2" 
                    value={formData.date}
                    onChange={handleChange}
                    min={today}
                    required 
                  />
              
                  <input 
                    type="text" 
                    name="query"
                    className="form-control mb-3" 
                    placeholder="Query:" 
                    value={formData.query}
                    onChange={handleChange}
                    maxLength="500"
                    required 
                  />
                  
                  <div className="form-check mb-3">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      name="consent"
                      id="consentCheckDesktop" 
                      checked={formData.consent} 
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="consentCheckDesktop">
                      <i className="fa-solid fa-circle-check me-1"></i>
                      I consent to get contacted from Lifeline Tulasi Hospital.
                    </label>
                  </div>

                  <div className="text-center">
                    <button type="submit" className="about-btn" disabled={submitting}>
                      {submitting ? "Sending..." : "Submit"}
                    </button>
                  </div>
                </form>


                 <form onSubmit={handleSubmit} className="d-block d-md-none">
                  <input type="hidden" name="doctor_name" value={formData.doctor_name} />
  <span className="mobile-label">Name</span>
                  <input 
                    type="text" 
                    name="name"
                    className="form-control mb-2" 
                 
                    value={formData.name}
                    onChange={handleChange}
                    maxLength="75"
                    required 
                  />
                  <span className="mobile-label">Phone Number</span>
                  <input 
                    type="text" 
                    name="phone"
                    className="form-control mb-2" 
               
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength="15"
                    required 
                  />
                    <span className="mobile-label">Preferred Date</span>
                  <input 
                    type="date" 
                    name="date"
                    className="form-control mb-2" 
                    value={formData.date}
                    onChange={handleChange}
                    min={today}
                    required 
                  />
                    <span className="mobile-label">Query</span>
                  <input 
                    type="text" 
                    name="query"
                    className="form-control mb-3" 
                
                    value={formData.query}
                    onChange={handleChange}
                    maxLength="500"
                    required 
                  />
                  
                  <div className="form-check mb-3">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      name="consent"
                      id="consentCheckMobile" 
                      checked={formData.consent} 
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="consentCheckMobile">
                      <i className="fa-solid fa-circle-check me-1"></i>
                      I consent to get contacted from Lifeline Tulasi Hospital.
                    </label>
                  </div>

                  <div className="text-center">
                    <button type="submit" className="about-btn" disabled={submitting}>
                      {submitting ? "Sending..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {Array.isArray(social_links) && social_links.length > 0 && (
          <div className="row mt-4">
            <div className="col-12 text-center">
              <div className="social-icons" data-sal="slide-up" data-sal-duration="1000">
                <span className="follow-us me-3">{footer.social_heading || "FOLLOW US:"}</span>
                {social_links.map((s, idx) => (
                  <a key={idx} href={s.url} className="me-2" target="_blank" rel="noopener noreferrer" aria-label={s.platform}>
                    <i className={socialIconFor(s.platform)} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ContactSection;